import React, { useState, useMemo } from "react";
import {
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
  eachDayOfInterval,
} from "date-fns";
import { fr } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  CheckCircle2,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Event, EventStatus } from "../../types/event.d";
import { cn } from "../../lib/utils";

interface OperationalCalendarProps {
  events: Event[];
  onEventClick?: (event: Event) => void;
}

type ViewMode = "month" | "week" | "day";

const OperationalCalendar: React.FC<OperationalCalendarProps> = ({
  events,
  onEventClick,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>("month");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const filteredEvents = useMemo(() => {
    if (activeFilters.length === 0) return events;
    return events.filter((event) => {
      if (activeFilters.includes("Mes événements")) return true; // Assuming all passed events are relevant for now
      if (
        activeFilters.includes("Validés") &&
        (event.status === EventStatus.COMPLETED ||
          event.status === EventStatus.PRESENCE_VALIDATED)
      )
        return true;
      if (
        activeFilters.includes("En attente") &&
        event.status === EventStatus.PENDING
      )
        return true;
      if (
        activeFilters.includes("Urgents") &&
        event.status === EventStatus.CONFIRMED
      )
        return true; // Mock logic for urgent
      return false;
    });
  }, [events, activeFilters]);

  const handlePrev = () => {
    if (viewMode === "month") setCurrentDate(subMonths(currentDate, 1));
    else if (viewMode === "week") setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };

  const handleNext = () => {
    if (viewMode === "month") setCurrentDate(addMonths(currentDate, 1));
    else if (viewMode === "week") setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const toggleFilter = (label: string) => {
    setActiveFilters((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  const renderHeader = () => {
    let dateText = "";
    if (viewMode === "month")
      dateText = format(currentDate, "MMMM yyyy", { locale: fr });
    else if (viewMode === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      dateText = `${format(start, "dd MMM")} - ${format(end, "dd MMM yyyy")}`;
    } else {
      dateText = format(currentDate, "dd MMMM yyyy", { locale: fr });
    }

    return (
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white rounded-t-2xl">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-bold text-slate-900 capitalize min-w-[150px]">
            {dateText}
          </h2>
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1">
            <button
              onClick={handlePrev}
              className="p-1 hover:bg-slate-100 rounded text-slate-500"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={handleNext}
              className="p-1 hover:bg-slate-100 rounded text-slate-500"
            >
              <ChevronRight size={18} />
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleToday}
            className="text-xs h-8"
          >
            Aujourd'hui
          </Button>
        </div>

        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewMode("month")}
            className={cn(
              "text-xs h-8 rounded-lg px-4",
              viewMode === "month" ? "bg-white shadow-sm" : "text-slate-500",
            )}
          >
            Mois
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewMode("week")}
            className={cn(
              "text-xs h-8 rounded-lg px-4",
              viewMode === "week" ? "bg-white shadow-sm" : "text-slate-500",
            )}
          >
            Semaine
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewMode("day")}
            className={cn(
              "text-xs h-8 rounded-lg px-4",
              viewMode === "day" ? "bg-white shadow-sm" : "text-slate-500",
            )}
          >
            Jour
          </Button>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    if (viewMode === "day") return null;
    const days = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
    return (
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-100">
        {days.map((day, i) => (
          <div
            key={i}
            className="py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400"
          >
            {day}
          </div>
        ))}
      </div>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });
    const rows: React.ReactNode[] = [];
    let days: React.ReactNode[] = [];

    calendarDays.forEach((day, i) => {
      const formattedDate = format(day, "d");
      const dayEvents = filteredEvents.filter((event) =>
        isSameDay(new Date(event.date), day),
      );

      days.push(
        <div
          key={day.toString()}
          className={cn(
            "min-h-[120px] bg-white border-r border-b border-slate-100 p-2 transition-colors hover:bg-slate-50/50 cursor-pointer relative group",
            !isSameMonth(day, monthStart) && "bg-slate-50 text-slate-300",
          )}
        >
          <div className="flex justify-between items-center mb-2">
            <span
              className={cn(
                "text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full",
                isSameDay(day, new Date())
                  ? "bg-rajapiBlue text-white"
                  : "text-slate-600",
                !isSameMonth(day, monthStart) && "text-slate-300",
              )}
            >
              {formattedDate}
            </span>
            {dayEvents.length > 0 && (
              <span className="w-1.5 h-1.5 bg-rajapiBlue rounded-full animate-pulse" />
            )}
          </div>

          <div className="space-y-1">
            {dayEvents.slice(0, 3).map((event) => (
              <div
                key={event.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventClick?.(event);
                }}
                className={cn(
                  "px-2 py-1 rounded text-[10px] font-medium truncate border shadow-sm transition-transform hover:scale-[1.02]",
                  event.status === EventStatus.CONFIRMED
                    ? "bg-blue-50 text-blue-700 border-blue-100"
                    : event.status === EventStatus.COMPLETED
                      ? "bg-green-50 text-green-700 border-green-100"
                      : "bg-slate-50 text-slate-700 border-slate-100",
                )}
              >
                {event.title}
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="text-[9px] text-slate-400 font-bold pl-1">
                + {dayEvents.length - 3} autres
              </div>
            )}
          </div>
        </div>,
      );

      if ((i + 1) % 7 === 0) {
        rows.push(
          <div className="grid grid-cols-7" key={day.toString()}>
            {days}
          </div>,
        );
        days = [];
      }
    });

    return (
      <div className="bg-white rounded-b-2xl overflow-hidden border-l border-t border-slate-100">
        {rows}
      </div>
    );
  };

  const renderWeekView = () => {
    const startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
    const endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    return (
      <div className="grid grid-cols-7 h-full border-l border-t border-slate-100">
        {days.map((day) => {
          const dayEvents = filteredEvents.filter((event) =>
            isSameDay(new Date(event.date), day),
          );
          return (
            <div
              key={day.toString()}
              className="min-h-[500px] bg-white border-r border-b border-slate-100 p-4 hover:bg-slate-50/50 transition-colors"
            >
              <div className="flex flex-col items-center mb-6">
                <span className="text-[10px] font-bold text-slate-400 uppercase mb-1">
                  {format(day, "EEE", { locale: fr })}
                </span>
                <span
                  className={cn(
                    "text-lg font-black w-10 h-10 flex items-center justify-center rounded-xl transition-all",
                    isSameDay(day, new Date())
                      ? "bg-rajapiBlue text-white shadow-lg shadow-blue-200"
                      : "text-slate-700",
                  )}
                >
                  {format(day, "d")}
                </span>
              </div>
              <div className="space-y-3">
                {dayEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => onEventClick?.(event)}
                    className={cn(
                      "p-3 rounded-xl border shadow-sm cursor-pointer transition-all hover:translate-y-[-2px] hover:shadow-md",
                      event.status === EventStatus.CONFIRMED
                        ? "bg-blue-50/50 border-blue-100 text-blue-700"
                        : event.status === EventStatus.COMPLETED
                          ? "bg-green-50/50 border-green-100 text-green-700"
                          : "bg-white border-slate-100 text-slate-700",
                    )}
                  >
                    <p className="text-[10px] uppercase font-bold opacity-60 mb-1">
                      {event.category}
                    </p>
                    <p className="text-xs font-bold leading-tight line-clamp-2">
                      {event.title}
                    </p>
                    <div className="mt-2 flex items-center gap-1 text-[9px] font-medium opacity-70">
                      <Clock size={10} />
                      <span>{format(new Date(event.date), "HH:mm")}</span>
                    </div>
                  </div>
                ))}
                {dayEvents.length === 0 && (
                  <div className="h-20 border-2 border-dashed border-slate-50 rounded-xl flex items-center justify-center">
                    <span className="text-[10px] text-slate-300 font-bold uppercase tracking-widest">
                      Libre
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = filteredEvents.filter((event) =>
      isSameDay(new Date(event.date), currentDate),
    );

    return (
      <div className="flex flex-col h-full bg-white border-l border-t border-slate-100 p-8">
        <div className="flex items-center gap-6 mb-10">
          <div
            className={cn(
              "w-20 h-20 rounded-3xl flex flex-col items-center justify-center shadow-xl",
              isSameDay(currentDate, new Date())
                ? "bg-rajapiBlue text-white shadow-blue-100"
                : "bg-slate-100 text-slate-800",
            )}
          >
            <span className="text-[10px] font-bold uppercase opacity-60 mb-1">
              {format(currentDate, "EEE")}
            </span>
            <span className="text-3xl font-black">
              {format(currentDate, "d")}
            </span>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-900">
              {format(currentDate, "LLLL yyyy", { locale: fr })}
            </h3>
            <p className="text-slate-400 font-medium">
              Vous avez {dayEvents.length} évènements programmés aujourd'hui.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {dayEvents.map((event) => (
            <div
              key={event.id}
              onClick={() => onEventClick?.(event)}
              className={cn(
                "group p-6 rounded-3xl border shadow-sm cursor-pointer transition-all hover:shadow-xl hover:translate-x-2 flex items-center justify-between",
                event.status === EventStatus.CONFIRMED
                  ? "bg-blue-50/30 border-blue-100"
                  : event.status === EventStatus.COMPLETED
                    ? "bg-green-50/30 border-green-100"
                    : "bg-white border-slate-100",
              )}
            >
              <div className="flex items-center gap-6">
                <div
                  className={cn(
                    "p-4 rounded-2xl",
                    event.status === EventStatus.CONFIRMED
                      ? "bg-rajapiBlue text-white"
                      : "bg-rajapiGreen text-white",
                  )}
                >
                  <Clock size={24} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge
                      variant={
                        event.status === EventStatus.CONFIRMED
                          ? "confirmed"
                          : "completed"
                      }
                      className="text-[9px] px-2"
                    >
                      {event.status}
                    </Badge>
                    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">
                      {format(new Date(event.date), "HH:mm")}
                    </span>
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 group-hover:text-rajapiBlue transition-colors">
                    {event.title}
                  </h4>
                  <p className="text-sm text-slate-500 max-w-xl line-clamp-1">
                    {event.description}
                  </p>
                </div>
              </div>
              <ChevronRight className="text-slate-200 group-hover:text-rajapiBlue group-hover:translate-x-1 transition-all" />
            </div>
          ))}
          {dayEvents.length === 0 && (
            <div className="py-20 flex flex-col items-center justify-center text-slate-300">
              <CalendarIcon size={64} className="mb-4 opacity-10" />
              <p className="text-sm font-bold uppercase tracking-widest opacity-40">
                Aucun évènement prévu
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCells = () => {
    if (viewMode === "month") return renderMonthView();
    if (viewMode === "week") return renderWeekView();
    return renderDayView();
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 w-full h-full">
      {/* Sidebar Filters */}
      <div className="w-full lg:w-64 flex-shrink-0 space-y-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-6 flex items-center gap-2">
            <CalendarIcon size={16} className="text-rajapiBlue" />
            Filtres Rapides
          </h3>

          <div className="space-y-2">
            {[
              {
                label: "Mes événements",
                count: events.length,
                color: "bg-rajapiBlue",
                icon: CalendarIcon,
              },
              {
                label: "Validés",
                count: events.filter(
                  (e) =>
                    e.status === EventStatus.COMPLETED ||
                    e.status === EventStatus.PRESENCE_VALIDATED,
                ).length,
                color: "bg-rajapiGreen",
                icon: CheckCircle2,
              },
              {
                label: "En attente",
                count: events.filter((e) => e.status === EventStatus.PENDING)
                  .length,
                color: "bg-rajapiYellow",
                icon: Clock,
              },
              {
                label: "Urgents",
                count: events.filter((e) => e.status === EventStatus.CONFIRMED)
                  .length,
                color: "bg-rajapiRed",
                icon: AlertCircle,
              },
            ].map((filter, i) => (
              <button
                key={i}
                onClick={() => toggleFilter(filter.label)}
                className={cn(
                  "w-full flex items-center justify-between p-3 rounded-xl transition-all group",
                  activeFilters.includes(filter.label)
                    ? "bg-slate-100 shadow-inner"
                    : "hover:bg-slate-50",
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "p-2 rounded-lg text-white transition-opacity",
                      filter.color,
                      activeFilters.length > 0 &&
                        !activeFilters.includes(filter.label) &&
                        "opacity-40",
                    )}
                  >
                    <filter.icon size={14} />
                  </div>
                  <span
                    className={cn(
                      "text-xs font-bold transition-colors",
                      activeFilters.includes(filter.label)
                        ? "text-slate-900"
                        : "text-slate-600 group-hover:text-slate-900",
                    )}
                  >
                    {filter.label}
                  </span>
                </div>
                <span className="text-[10px] font-bold bg-white text-slate-400 px-2 py-0.5 rounded-full shadow-sm">
                  {filter.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Small statistics card */}
        <div className="bg-rajapiBlue/5 p-6 rounded-2xl border border-rajapiBlue/10">
          <h4 className="text-[10px] font-bold text-rajapiBlue uppercase tracking-widest mb-3">
            Statistiques
          </h4>
          <div className="space-y-4">
            <div>
              <p className="text-2xl font-black text-rajapiBlue">
                {Math.round(
                  (events.filter(
                    (e) =>
                      e.status === EventStatus.COMPLETED ||
                      e.status === EventStatus.PRESENCE_VALIDATED,
                  ).length /
                    (events.length || 1)) *
                    100,
                )}
                %
              </p>
              <p className="text-[10px] text-slate-500 font-medium">
                Taux de présence validée
              </p>
            </div>
            <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-rajapiBlue transition-all duration-500"
                style={{
                  width: `${(events.filter((e) => e.status === EventStatus.COMPLETED || e.status === EventStatus.PRESENCE_VALIDATED).length / (events.length || 1)) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Calendar Grid Area */}
      <div className="flex-1 min-w-0 flex flex-col h-full bg-white rounded-2xl border border-slate-200 shadow-sm lg:overflow-hidden">
        {renderHeader()}
        {renderDays()}
        <div className="flex-1 overflow-y-auto">{renderCells()}</div>
      </div>
    </div>
  );
};

export default OperationalCalendar;
